import React, { useEffect, useRef, useState } from "react";
import {
  ord, num, placeLabel, roundsOf, roundOf, markOf, isSingleJudge, hasRecall, placedOf, sectionOf, SECTION_LABEL, statusOf, longTagsOf, outcomeOf,
} from "./oireachtasResultsModel";

// Presentational pieces of the Oireachtas results pages (MC2 card f157ed22),
// in the feis results design (eventResults.css, er- classes) plus a few
// Oireachtas-only rules (oireachtasResults.css, or- classes). No blue.

const Msr = ({ children, className = "" }) => <span className={`msr ${className}`} aria-hidden="true">{children}</span>;

const initials = (name) =>
  String(name || "").replace(/\(.*?\)/g, "").split(/[\s-]+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";

const Avatar = ({ name, photo, small }) =>
  photo ? <img className={`er-av${small ? " or-av-sm" : ""}`} src={photo} alt="" />
    : <span className={`er-av${small ? " or-av-sm" : ""}`} aria-hidden="true">{initials(name)}</span>;

// Place pill: medal + 1st/2nd/3rd in gold / silver / bronze, "3T" on a tie.
export const PlacePill = ({ position, tied }) => (
  <span className={`er-place ${position <= 3 ? `p${position}` : ""}`} title={tied ? "Tied" : undefined}>
    <Msr>workspace_premium</Msr>
    {placeLabel(position, tied)}
    {tied && <span className="er-sr-only"> (tied)</span>}
  </span>
);

const Outcome = ({ r }) => {
  const o = outcomeOf(r);
  if (o.kind === "place") return <PlacePill position={o.position} tied={o.tied} />;
  if (o.kind === "recalled") return <span className="er-pill mine">Recalled</span>;
  if (o.kind === "placed") return <span className="er-pill mine">{o.text}</span>;
  return <span className="er-place np">{o.text}</span>;
};

// One competition in a list: opens its own page.
export const CompRow = ({ comp, onOpen, note }) => (
  <button type="button" className="er-comp-h or-row" onClick={onOpen}>
    <span className="er-code">{comp.code}</span>
    <span className="er-name">{comp.name}</span>
    <span className="er-mine-slot">{note}</span>
    <span className="er-st">
      {comp.state === "recall_published"
        ? <span className="er-pill pending">Recall list</span>
        : comp.state === "places_published"
          ? <span className="er-pill pending">Places</span>
          : <span className="er-pill or-final">Final results</span>}
    </span>
    <Msr className="er-chev">chevron_right</Msr>
  </button>
);

// My Results / Your students: a card per dancer, a row per competition with a
// published recall list or results; each row opens the dancer's own page.
export const DancerCards = ({ dancers, onOpen }) => (
  <div className="er-mine-grid">
    {dancers.map((d) => (
      <div key={d.dancer_id} className="er-mcard">
        <h3>{d.name}</h3>
        {d.results.length === 0 && <div className="or-none">No results published yet.</div>}
        {d.results.map((r) => (
          <button key={r.competition_id} type="button" className="er-mrow" onClick={() => onOpen(r)}>
            <span className="er-code">{r.code}</span>
            <span className="er-mname">{r.name}<small>#{r.card}{r.team ? ` · ${r.team.name}` : ""}{r.state === "recall_published" || r.state === "places_published" ? " · final results to come" : ""}</small></span>
            <span className="er-mright">
              <Outcome r={r} />
              <Msr className="er-mchev">chevron_right</Msr>
            </span>
          </button>
        ))}
      </div>
    ))}
  </div>
);

// A recall list: the recalled dancers in card-number order, with name and
// school (no scores).
export const RecallNumbers = ({ detail, mineCards }) => {
  // card-number order (the server sends it so; kept here so it can never read as standings)
  const rows = [...(detail.recalled || [])].sort((a, b) => Number(a.card) - Number(b.card) || String(a.card).localeCompare(String(b.card)));
  return (
    <div className="or-card">
      <div className="or-recall-head">
        <b>Recalled</b>
        <span>{rows.length} dancer{rows.length === 1 ? "" : "s"} · final results to come</span>
      </div>
      <div className="er-rt or-recall" role="table" aria-label="Recalled dancers">
        <div className="er-rr th" role="row"><div role="columnheader">Card</div><div role="columnheader">Dancer</div></div>
        {rows.map((r) => (
          <div key={r.card} className={`er-rr${mineCards.has(r.card) ? " me" : ""}`} role="row">
            <div role="cell" className="or-card-no">{r.card}</div>
            <div role="cell" className="er-nm">{r.name}{r.school && <div className="or-rsc">{r.school}</div>}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// A one-round competition's places list (Devin 2026-11-04): everyone who
// placed, in card-number order, with name and school; no places, no scores
// (the full results follow the awards).
export const PlacesNumbers = ({ detail, mineCards }) => {
  // card-number order (the server sends it so; kept here so it can never read as standings)
  const rows = [...(detail.places || [])].sort((a, b) => Number(a.card) - Number(b.card) || String(a.card).localeCompare(String(b.card)));
  return (
    <div className="or-card">
      <div className="or-recall-head">
        <b>Places</b>
        <span>{rows.length} placed · in numerical order · final results to come</span>
      </div>
      <div className="er-rt or-recall" role="table" aria-label="Placed">
        <div className="er-rr th" role="row"><div role="columnheader">Card</div><div role="columnheader">Name</div></div>
        {rows.map((r) => (
          <div key={r.card} className={`er-rr${mineCards.has(r.card) ? " me" : ""}`} role="row">
            <div role="cell" className="or-card-no">{r.card}</div>
            <div role="cell" className="er-nm">{r.name}{r.school && <div className="or-rsc">{r.school}</div>}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// The round awards as they are posted (Casey Costello's ECRO 2026 process,
// Devin 2026-10-29): with the recall, each round's winners by card number,
// no ranking (dancers know they won one, not where); with the full result,
// ranked from last to first by the drawn judge's mark. One card, a column
// per round side by side on a wide screen and a tab per round on a phone;
// one short line per winner (Devin 2026-10-30: the rows ran three phone
// screens a round, and the shared results-row grid put the name before the
// card number). Own classes (or-aw-), not the results table's er-rr.
export const RoundAwards = ({ detail, mineCards }) => {
  const lists = (detail.roundAwards || []).filter((l) => (l.winners || []).length);
  const [tab, setTab] = useState(null);
  if (!lists.length) return null;
  const ranked = detail.state === "results_published";
  const shown = lists.some((l) => l.round === tab) ? tab : lists[0].round;
  // with the recall in card order, kept here so the list can never read as a
  // ranking (as RecallNumbers does); ranked: the server's last-to-first order
  const byCard = (ws) => [...ws].sort((a, b) => Number(a.card) - Number(b.card) || String(a.card).localeCompare(String(b.card)));
  // ranked: 1st at the top (Devin 2026-10-30; the posted sheet still reads
  // last to first), ties side by side in card order
  const byPlace = (ws) => [...ws].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || Number(a.card) - Number(b.card));
  const count = (n) => `${n} dancer${n === 1 ? "" : "s"}`;
  return (
    <div className="or-card or-awards">
      <div className="or-recall-head">
        <b>Round medals</b>
        <span>{ranked ? "in order of placing" : "in card order"} · collect at Headquarters</span>
      </div>
      {lists.length > 1 && (
        // two toggle buttons, not ARIA tabs (no arrow-key tab pattern needed for two rounds; MC review 426b153b R1-F2)
        <div className="or-aw-tabs" role="group" aria-label="Round">
          {lists.map((list) => (
            <button
              key={list.round}
              type="button"
              aria-pressed={list.round === shown}
              className={`or-aw-tab${list.round === shown ? " on" : ""}`}
              onClick={() => setTab(list.round)}
            >
              Round {list.round}
            </button>
          ))}
        </div>
      )}
      <div className={`or-aw-cols${lists.length === 1 ? " single" : ""}`}>
        {lists.map((list) => (
          <div key={list.round} className={`or-aw-col${list.round === shown ? " on" : ""}`}>
            <div className="or-aw-round">
              Round {list.round}
              <span>{count(list.winners.length)}</span>
            </div>
            {/* the grid's own look (Devin 2026-10-30): place bubble with the
                card under it, name over school, striped rows; no marks */}
            <table className="or-table or-medals" aria-label={`Round ${list.round} awards`}>
              <thead>
                <tr>
                  <th scope="col" className="or-c-place">{ranked ? "Place" : "Card"}</th>
                  <th scope="col" className="or-c-name">Dancer</th>
                </tr>
              </thead>
              <tbody>
                {(ranked ? byPlace(list.winners) : byCard(list.winners)).map((w, i) => (
                  <tr key={w.card} className={[mineCards.has(w.card) ? "me" : "", i % 2 ? "alt" : ""].filter(Boolean).join(" ")}>
                    <td className="or-c-place">
                      {ranked && w.rank != null && <PlacePill position={w.rank} tied={w.tied} />}
                      <div className="or-cardno">#{w.card}</div>
                    </td>
                    <td className="or-c-name"><div className="or-nm">{w.name}</div>{w.school && <div className="or-sc">{w.school}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

// The full published table (Devin 2026-10-27): the feis championship table
// blended with the official results sheets. Place (card number under it),
// dancer (school under it), total Irish points and status (WQ / NQ) first,
// place and dancer pinned; then each round's marks as score/IP with the
// round total, that judge's place for the dancer under each mark. Counted
// marks bold, the marks the high/low drop removed in grey, green lines
// between rounds, green totals, every other row striped (as on the print).
// With a recall every recalled dancer is placed, so the only divider is
// "Not recalled" ("Did not place" without a recall).
// Rows don't open anything (Devin 2026-10-27). A judge's own seats are highlighted,
// and the table opens scrolled to the first of them.
export const FullTable = ({ detail, mineCards, seats }) => {
  if (isSingleJudge(detail)) return <SingleJudgeTable detail={detail} mineCards={mineCards} seats={seats} />;
  return <PanelTable detail={detail} mineCards={mineCards} seats={seats} />;
};

// One judge, one round: Place | Dancer | Score (the judge's raw score; no
// Irish points, as on the feis single-judge table) | Status.
const SingleJudgeTable = ({ detail, mineCards, seats }) => {
  const [{ round, judges }] = roundsOf(detail);
  const judge = judges[0];
  const mine = seats?.has(`${round}-${judge.slot}`);
  const showStatus = detail.rows.some((r) => statusOf(r).length > 0);
  let lastSection = "placed";
  return (
    <>
      <div className="er-rt-head">
        <span className="er-note">{detail.rows.length} dancers{detail.rows.some((r) => r.placed) ? ` · ${detail.rows.filter((r) => r.placed).length} placed` : ""}</span>
        <span className="er-judge1">Judge: <b>{judge.name || judge.slot}</b></span>
      </div>
      <div className="or-tw" tabIndex={0} role="region" aria-label="Results table (scrolls sideways)">
        <table className="or-table single">
          <thead>
            <tr>
              <th scope="col" className="or-c-place or-pin">Place</th>
              <th scope="col" className="or-c-name or-pin">Dancer</th>
              <th scope="col" className={`or-c-score${mine ? " me-col" : ""}`}>Score{mine && <span className="er-sr-only"> (your marks)</span>}</th>
              {showStatus && <th scope="col" className="or-c-status">Status</th>}
            </tr>
          </thead>
          <tbody>
            {detail.rows.map((r, i) => {
              const section = sectionOf(r, false);
              const divider = section !== lastSection && section !== "placed" ? SECTION_LABEL[section] : null;
              lastSection = section;
              const m = markOf(r, round, judge.slot);
              const rr = roundOf(r, round);
              const status = statusOf(r);
              return (
                <React.Fragment key={r.card}>
                  {divider && <tr className="or-divider"><td colSpan={showStatus ? 4 : 3}><span>{divider}</span></td></tr>}
                  <tr className={[mineCards.has(r.card) ? "me" : "", r.placed ? "" : "unplaced", i % 2 ? "alt" : ""].filter(Boolean).join(" ")}>
                    <td className="or-c-place or-pin">
                      {r.placed ? <PlacePill position={r.position} tied={r.tied} />
                        : <span className="er-place plain"><Msr className="slot">workspace_premium</Msr>{r.position ? placeLabel(r.position, r.tied) : ""}</span>}
                      <div className="or-cardno">#{r.card}</div>
                    </td>
                    <td className="or-c-name or-pin"><div className="or-nm">{r.name}</div><div className="or-sc">{r.school}</div></td>
                    <td className={`or-c-score${mine ? " me-col" : ""}`}>{rr?.noShow ? "No show" : m ? num(m.score) : ""}</td>
                    {showStatus && <td className="or-c-status">{status.map((t) => <span key={t} className="or-st">{t}</span>)}</td>}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {(showStatus || detail.rows.some((r) => r.tied)) && (
        <p className="or-legend">T = tied{showStatus ? " · WM = World medal holder · WQ = World qualifier · NQ = NAIDC qualifier · FTC = Failed to complete" : ""}</p>
      )}
    </>
  );
};

const PanelTable = ({ detail, mineCards, seats }) => {
  const ref = useRef(null);
  const rounds = roundsOf(detail);
  const multi = hasRecall(detail);
  const star = detail.competition.dropHighLow ? "*" : "";
  const seatClass = (round, slot) => (seats?.has(`${round}-${slot}`) ? " me-col" : "");
  // the Status column only when some dancer here has a status (Devin 2026-10-27)
  const showStatus = detail.rows.some((r) => statusOf(r).length > 0);
  const span = (showStatus ? 4 : 3) + rounds.reduce((n, { judges }) => n + judges.length + 1, 0);

  // once per competition (not on every minute refresh, which would pull a
  // judge back while they read another round)
  const scrolledFor = useRef(null);
  const compKey = String(detail.competition?._id || detail.competition?.code || "");
  useEffect(() => {
    const wrap = ref.current;
    const cell = wrap?.querySelector("th.me-col");
    if (!wrap || !cell || scrolledFor.current === compKey) return;
    scrolledFor.current = compKey;
    // place and dancer stay pinned: bring the judge's first column in beside them
    const pinned = [...wrap.querySelectorAll("thead tr:first-child th.or-pin")].reduce((w, el) => w + el.offsetWidth, 0);
    wrap.scrollLeft = Math.max(0, cell.offsetLeft - pinned - 8);
  }, [seats, compKey]);

  let lastSection = "placed";
  return (
    <>
      <div className="er-rt-head">
        <span className="er-note">{detail.rows.length} dancers{detail.rows.some((r) => placedOf(r, multi)) ? ` · ${detail.rows.filter((r) => placedOf(r, multi)).length} placed` : ""}</span>
        <span className="er-scroll-hint">Swipe for the rounds<Msr>arrow_forward</Msr></span>
      </div>
      <div ref={ref} className="or-tw" tabIndex={0} role="region" aria-label="Results table (scrolls sideways)">
        <table className="or-table">
          <thead>
            <tr>
              <th scope="col" rowSpan={2} className="or-c-place or-pin">Place</th>
              <th scope="col" rowSpan={2} className="or-c-name or-pin">Dancer</th>
              <th scope="col" rowSpan={2} className="or-c-total">Total IP</th>
              {showStatus && <th scope="col" rowSpan={2} className="or-c-status">Status</th>}
              {rounds.map(({ round, judges }) => (
                <th key={round} scope="colgroup" colSpan={judges.length + 1} className="or-c-round">Round {round} <span>· Score/IP</span></th>
              ))}
            </tr>
            <tr className="or-judges">
              {rounds.map(({ round, judges }) => (
                <React.Fragment key={round}>
                  {judges.map((j, k) => (
                    <th key={j.slot} scope="col" className={`or-c-j${k === 0 ? " or-rs" : ""}${seatClass(round, j.slot)}`} title={j.name}>
                      {j.name || j.slot}{seats?.has(`${round}-${j.slot}`) && <span className="er-sr-only"> (your marks)</span>}
                    </th>
                  ))}
                  <th scope="col" className="or-c-rt">Total{star}</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {detail.rows.map((r, i) => {
              const section = sectionOf(r, multi);
              const divider = section !== lastSection && section !== "placed" ? SECTION_LABEL[section] : null;
              lastSection = section;
              const status = statusOf(r);
              return (
                <React.Fragment key={r.card}>
                  {divider && (
                    <tr className="or-divider"><td colSpan={span}><span>{divider}</span></td></tr>
                  )}
                  <tr className={[mineCards.has(r.card) ? "me" : "", placedOf(r, multi) ? "" : "unplaced", i % 2 ? "alt" : ""].filter(Boolean).join(" ")}>
                    <td className="or-c-place or-pin">
                      {placedOf(r, multi) ? <PlacePill position={r.position} tied={r.tied} />
                        : <span className="er-place plain"><Msr className="slot">workspace_premium</Msr>{r.position ? placeLabel(r.position, r.tied) : ""}</span>}
                      <div className="or-cardno">#{r.card}</div>
                    </td>
                    <td className="or-c-name or-pin"><div className="or-nm">{r.name}</div><div className="or-sc">{r.school}</div></td>
                    <td className="or-c-total">{num(r.totalPoints)}</td>
                    {showStatus && <td className="or-c-status">{status.map((t) => <span key={t} className="or-st">{t}</span>)}</td>}
                    {rounds.map(({ round, judges }) => {
                      const rr = roundOf(r, round);
                      return (
                        <React.Fragment key={round}>
                          {judges.map((j, k) => {
                            const m = markOf(r, round, j.slot);
                            return (
                              <td key={j.slot} className={`or-c-j${k === 0 ? " or-rs" : ""}${m?.dropped ? " or-drop" : ""}${seatClass(round, j.slot)}`}>
                                {rr && !rr.noShow && m ? <>{num(m.score)}/{num(m.points)}{m.rank ? <small><span className="er-sr-only">, place </span>{ord(m.rank)}</small> : null}{m.dropped && <span className="er-sr-only"> (dropped)</span>}</> : ""}
                              </td>
                            );
                          })}
                          <td className="or-c-rt">{rr ? (rr.noShow ? "NS" : num(rr.total)) : ""}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="or-legend">
        T = tied · WM = World medal holder · WQ = World qualifier · NQ = NAIDC qualifier · FTC = Failed to complete · Score/IP = the judge&apos;s mark / Irish points, with that judge&apos;s place under it.{star ? " * Round totals leave out each dancer's high and low Irish points (in grey)." : ""}
      </p>
    </>
  );
};

// One dancer's page (Devin 2026-10-27): their place, a box per round with
// every judge's mark, then the full results table with their row highlighted
// (and a judge's own columns, for a judge).
export const DancerSheet = ({ detail, card, mineCards, seats }) => {
  const row = (detail.rows || []).find((r) => r.card === card);
  if (!row) return <div className="er-empty">This dancer isn&apos;t in this competition&apos;s results.</div>;
  const rounds = roundsOf(detail);
  const multi = hasRecall(detail);
  const single = isSingleJudge(detail);
  const star = detail.competition.dropHighLow ? "*" : "";
  const tags = longTagsOf(row, multi);
  return (
    <div className="or-sheet">
      <div className="or-placebox">
        <div className="or-big">
          {placedOf(row, multi) ? `${placeLabel(row.position, row.tied)} place` : multi && !row.recalled ? "Not recalled" : (row.status || []).includes("FTC") ? "Did not complete" : "Not placed"}
        </div>
        <div className="or-total">{single ? `Score ${num(markOf(row, rounds[0].round, rounds[0].judges[0].slot)?.score)}` : `${num(row.totalPoints)} Irish points`}</div>
        {tags.length > 0 && <div className="or-ptags">{tags.map((t) => <span key={t} className="or-ptag">{t}</span>)}</div>}
      </div>

      <h2 className="or-h2">Marks</h2>
      <div className={`or-rounds n${rounds.length}`}>
        {rounds.map(({ round, judges }) => {
          const rr = roundOf(row, round);
          return (
            <div key={round} className="or-round">
              <div className="or-rh">Round {round}</div>
              {!rr ? (
                <div className="or-none">{multi && !row.recalled ? "Not recalled for this round" : "Not danced"}</div>
              ) : rr.noShow ? (
                <div className="or-none">No show</div>
              ) : (
                <>
                  <div className={`or-jrow head${single ? " single" : ""}`}><span /><span>Judge</span><span>Score</span><span>Place</span>{!single && <span>IP</span>}</div>
                  {judges.map((j) => {
                    const m = markOf(row, round, j.slot);
                    return (
                      <div key={j.slot} className={`or-jrow${single ? " single" : ""}${m?.dropped ? " or-drop" : ""}`} title={m?.dropped ? `Dropped (${m.dropped === "high" ? "highest" : "lowest"} Irish points)` : undefined}>
                        <Avatar name={j.name} photo={j.photo} small />
                        <span className="or-jn">{j.name || j.slot}</span>
                        <span>{num(m?.score)}</span>
                        <span className="or-jp">{m?.rank ? ord(m.rank) : "–"}</span>
                        {!single && <b>{num(m?.points)}{m?.dropped && <span className="er-sr-only"> (dropped)</span>}</b>}
                      </div>
                    );
                  })}
                  {!single && <div className="or-rtot"><span>Round {round} total{star}</span><b>{num(rr.total)}</b></div>}
                </>
              )}
            </div>
          );
        })}
      </div>
      {star && <p className="or-legend">* Round totals leave out the high and low Irish points (in grey).</p>}

      <h2 className="or-h2">Full results</h2>
      <div className="or-card or-tablecard">
        <FullTable detail={detail} mineCards={new Set([card, ...(mineCards || [])])} seats={seats} />
      </div>
    </div>
  );
};
